/**
 * Washing machine listing spec (Wave 2 appliance inverter + loader type Phase 1).
 *
 * Canonical id: `washing-machine`. Legacy `inverter-washing-machine` aliases.
 */

import {
  composeListingTitleFromSlotsWithIdentity,
  inverterTitleToken,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";
import {
  APPLIANCE_INVERTER_TYPES,
  CANONICAL_WASHING_MACHINE_SUBCATEGORY_ID,
  getLegacySubcategoryInverterMapping,
  isApplianceInverterType,
  LEGACY_INVERTER_WASHING_MACHINE_SUBCATEGORY_ID,
  pushApplianceInverterInvalidIssue,
  pushApplianceInverterRequiredIssue,
  resolveInverterTypeForLegacySubcategory,
  type ApplianceInverterType,
} from "@/lib/listing/applianceInverterListingSpec";
import { asListingSpecAttributes } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";

export const WASHER_LOADER_TYPES = ["top_loader", "front_loader"] as const;
export type WasherLoaderType = (typeof WASHER_LOADER_TYPES)[number];

const FRONT_LOADER_TITLE_RE = /\bfront[\s-]?load(?:ers?|ing)?\b/i;
/** Plural `top loaders` included (D6 miss). */
const TOP_LOADER_TITLE_RE = /\btop[\s-]?load(?:ers?|ing)?\b/i;
/** Ops 2026-08-29: twin tub / twin tube → top_loader. */
const TWIN_TUB_TITLE_RE = /\btwin[\s-]?tub(?:e|es)?\b/i;

export const WASHING_MACHINE_SUBCATEGORY_IDS = [
  CANONICAL_WASHING_MACHINE_SUBCATEGORY_ID,
  LEGACY_INVERTER_WASHING_MACHINE_SUBCATEGORY_ID,
] as const;

export type WashingMachineSubcategoryId =
  (typeof WASHING_MACHINE_SUBCATEGORY_IDS)[number];

export const WASHING_MACHINE_LOAD_KG_MIN = 1;
export const WASHING_MACHINE_LOAD_KG_MAX = 25;

export type WashingMachineSpecFormState = {
  loadCapacityKg: number | "";
  applianceInverterType: ApplianceInverterType | "";
  washerLoaderType: WasherLoaderType | "";
};

export function defaultWashingMachineSpecFormState(): WashingMachineSpecFormState {
  return {
    loadCapacityKg: "",
    applianceInverterType: "",
    washerLoaderType: "",
  };
}

export function isWasherLoaderType(value: unknown): value is WasherLoaderType {
  return (
    typeof value === "string" &&
    (WASHER_LOADER_TYPES as readonly string[]).includes(value)
  );
}

export function isWashingMachineSubcategory(
  subcategoryId: string | null | undefined,
): subcategoryId is WashingMachineSubcategoryId {
  return (
    typeof subcategoryId === "string" &&
    (WASHING_MACHINE_SUBCATEGORY_IDS as readonly string[]).includes(
      subcategoryId,
    )
  );
}

export function getWashingMachineSpecSectionLabel(): string {
  return "Washing machine details";
}

/** Common load pills for Step 2; sellers can still type other values. */
export const WASHING_MACHINE_LOAD_KG_PRESETS = [6, 7, 8, 9, 10, 12] as const;

function formatKgForTitle(kg: number): string {
  if (Number.isInteger(kg)) return String(kg);
  return String(kg);
}

export function formatWasherLoaderTitleWord(
  loaderType: WasherLoaderType,
): string {
  return loaderType === "top_loader" ? "Top Load" : "Front Load";
}

/** D6 title inference — returns null when ambiguous or no signal. */
export function inferWasherLoaderTypeFromTitle(
  title: string | null | undefined,
): WasherLoaderType | null {
  const t = String(title ?? "");
  const hasFront = FRONT_LOADER_TITLE_RE.test(t);
  const hasTop = TOP_LOADER_TITLE_RE.test(t) || TWIN_TUB_TITLE_RE.test(t);
  if (hasFront && hasTop) return null;
  if (hasFront) return "front_loader";
  if (hasTop) return "top_loader";
  return null;
}

function pushWasherLoaderRequiredIssue(
  issues: ListingSpecValidationIssue[],
): void {
  issues.push({
    key: "washer_loader_type",
    code: "required",
    message: "Choose top load or front load.",
  });
}

function pushWasherLoaderInvalidIssue(
  issues: ListingSpecValidationIssue[],
): void {
  issues.push({
    key: "washer_loader_type",
    code: "invalid_enum",
    message: "Choose top load or front load.",
  });
}

/** Locked: `{kg}kg` → Inverter? → loader → brand? → Washing Machine. */
export function composeWashingMachineTitleFromAttributes(
  attrs: ListingSpecAttributes,
  identity?: ListingTitleIdentity | null,
): string {
  const kg = attrs.load_capacity_kg;
  const inverter = attrs.appliance_inverter_type;
  const loader = attrs.washer_loader_type;
  if (typeof kg !== "number" || !Number.isFinite(kg)) return "";
  if (!isApplianceInverterType(inverter)) return "";
  if (!isWasherLoaderType(loader)) return "";

  return composeListingTitleFromSlotsWithIdentity(
    {
      capacity: `${formatKgForTitle(kg)}kg`,
      inverter: inverterTitleToken(inverter),
      form: formatWasherLoaderTitleWord(loader),
      product: "Washing Machine",
    },
    identity,
  );
}

export function buildWashingMachineAttributesFromForm(
  form: WashingMachineSpecFormState,
): ListingSpecAttributes {
  const out: ListingSpecAttributes = {};
  if (
    typeof form.loadCapacityKg === "number" &&
    Number.isFinite(form.loadCapacityKg)
  ) {
    out.load_capacity_kg = form.loadCapacityKg;
  }
  if (isApplianceInverterType(form.applianceInverterType)) {
    out.appliance_inverter_type = form.applianceInverterType;
  }
  if (isWasherLoaderType(form.washerLoaderType)) {
    out.washer_loader_type = form.washerLoaderType;
  }
  return out;
}

export function getWashingMachineSpecFormValidationMessage(
  form: WashingMachineSpecFormState,
): string | undefined {
  const issues = validateStoredWashingMachineAttributes(
    buildWashingMachineAttributesFromForm(form),
  );
  return issues[0]?.message;
}

export type WashingMachineFormHydrateOptions = {
  subcategoryId?: string | null;
  title?: string | null;
};

/** Hydrate sell/edit form from stored `items.attributes` (+ legacy alias fallback). */
export function washingMachineFormFromAttributes(
  attributes: unknown,
  options?: WashingMachineFormHydrateOptions,
): WashingMachineSpecFormState {
  const base = defaultWashingMachineSpecFormState();
  const attrs = asListingSpecAttributes(attributes);
  const kg = attrs.load_capacity_kg;
  const inverter = attrs.appliance_inverter_type;
  const loader = attrs.washer_loader_type;
  let applianceInverterType: ApplianceInverterType | "" =
    isApplianceInverterType(inverter) ? inverter : base.applianceInverterType;
  const legacyMap = getLegacySubcategoryInverterMapping(options?.subcategoryId);
  if (
    !applianceInverterType &&
    legacyMap &&
    legacyMap.legacyId !== legacyMap.canonicalId &&
    options?.subcategoryId
  ) {
    applianceInverterType = resolveInverterTypeForLegacySubcategory(
      options.subcategoryId,
      options.title,
    );
  }
  let washerLoaderType: WasherLoaderType | "" = isWasherLoaderType(loader)
    ? loader
    : base.washerLoaderType;
  if (!washerLoaderType && options?.title) {
    washerLoaderType = inferWasherLoaderTypeFromTitle(options.title) ?? "";
  }
  return {
    loadCapacityKg:
      typeof kg === "number" && Number.isFinite(kg) ? kg : base.loadCapacityKg,
    applianceInverterType,
    washerLoaderType,
  };
}

export function validateStoredWashingMachineAttributes(
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  const issues: ListingSpecValidationIssue[] = [];
  const attrs = attributes as ListingSpecAttributes;
  const kg = attrs.load_capacity_kg;
  const inverter = attrs.appliance_inverter_type;
  const loader = attrs.washer_loader_type;

  if (kg === undefined || kg === null || kg === "") {
    issues.push({
      key: "load_capacity_kg",
      code: "required",
      message: "Load capacity (kg) is required.",
    });
  } else if (typeof kg !== "number" || !Number.isFinite(kg)) {
    issues.push({
      key: "load_capacity_kg",
      code: "invalid_type",
      message: "Please enter a valid load capacity in kg.",
    });
  } else if (
    kg < WASHING_MACHINE_LOAD_KG_MIN ||
    kg > WASHING_MACHINE_LOAD_KG_MAX
  ) {
    issues.push({
      key: "load_capacity_kg",
      code: "out_of_range",
      message: `Load capacity must be between ${WASHING_MACHINE_LOAD_KG_MIN} and ${WASHING_MACHINE_LOAD_KG_MAX} kg.`,
    });
  }

  if (inverter === undefined || inverter === null || inverter === "") {
    pushApplianceInverterRequiredIssue(issues);
  } else if (!isApplianceInverterType(inverter)) {
    pushApplianceInverterInvalidIssue(issues);
  }

  if (loader === undefined || loader === null || loader === "") {
    pushWasherLoaderRequiredIssue(issues);
  } else if (!isWasherLoaderType(loader)) {
    pushWasherLoaderInvalidIssue(issues);
  }

  return issues;
}

export { APPLIANCE_INVERTER_TYPES };
